import { useCallback, useEffect, useRef, useState } from 'react'

import AnswersApiService from '@/apis/answersApiService'
import useAnswerStreamContext from '@/hooks/useAnswerStreamContext'
import type { CableEvent } from '@/types/cable'
import type { Message } from '@/types/chat'

const EMPTY_MESSAGES: Message[] = []

interface UseChatCableOptions {
  initialMessages?: Message[]
  conversationId: string | null
  initialActiveUserMessageId?: string | null
  onConversationCreated?: (id: string) => void
}

export default function useChatCable({
  initialMessages = EMPTY_MESSAGES,
  conversationId,
  initialActiveUserMessageId,
  onConversationCreated,
}: UseChatCableOptions) {
  const [messages, setMessages] = useState<Message[]>(initialMessages)
  const [streamingContent, setStreamingContent] = useState('')
  const [isStreaming, setIsStreaming] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const conversationIdRef = useRef(conversationId)
  const activeUserMessageIdRef = useRef<string | null>(null)
  const chunksRef = useRef<Map<number, string>>(new Map())

  const { subscribe, markPending, clearPending, getPending } = useAnswerStreamContext()

  useEffect(() => {
    conversationIdRef.current = conversationId
  }, [conversationId])

  useEffect(() => {
    setMessages(initialMessages)
  }, [initialMessages])

  const resetStream = useCallback(() => {
    activeUserMessageIdRef.current = null
    chunksRef.current.clear()
    setStreamingContent('')
    setIsStreaming(false)
  }, [])

  const handleEvent = useCallback(
    (event: CableEvent) => {
      if (event.type === 'conversation_named') return
      if (event.conversationId !== conversationIdRef.current) return
      if (
        activeUserMessageIdRef.current &&
        event.userMessageId !== activeUserMessageIdRef.current
      ) {
        return
      }

      if (!activeUserMessageIdRef.current) {
        activeUserMessageIdRef.current = event.userMessageId
        setIsStreaming(true)
      }

      switch (event.type) {
        case 'meta':
          // Citations are also included in the final message; nothing to render yet.
          break
        case 'chunk': {
          chunksRef.current.set(event.seq, event.delta)
          const ordered = [...chunksRef.current.entries()]
            .sort(([a], [b]) => a - b)
            .map(([, delta]) => delta)
            .join('')
          setStreamingContent(ordered)
          break
        }
        case 'done':
          setMessages((prev) => [...prev, event.message])
          chunksRef.current.clear()
          activeUserMessageIdRef.current = null
          clearPending(event.conversationId)
          setStreamingContent('')
          setIsStreaming(false)
          break
        case 'error':
          setError(event.message)
          chunksRef.current.clear()
          activeUserMessageIdRef.current = null
          clearPending(event.conversationId)
          setStreamingContent('')
          setIsStreaming(false)
          break
        default:
          break
      }
    },
    [clearPending],
  )

  const handleEventRef = useRef(handleEvent)
  useEffect(() => {
    handleEventRef.current = handleEvent
  }, [handleEvent])

  useEffect(() => subscribe((event) => handleEventRef.current(event)), [subscribe])

  useEffect(() => {
    if (!conversationId) return
    const pending = initialActiveUserMessageId ?? getPending(conversationId)
    if (pending && !activeUserMessageIdRef.current) {
      activeUserMessageIdRef.current = pending
      setIsStreaming(true)
    }
  }, [conversationId, initialActiveUserMessageId, getPending])

  const sendMessage = useCallback(
    async (question: string) => {
      if (isStreaming) return

      setError(null)
      setIsStreaming(true)
      setStreamingContent('')
      chunksRef.current.clear()

      const userMessage: Message = {
        id: `temp-${Date.now()}`,
        role: 'user',
        content: question,
        citations: null,
        audioUrl: null,
        createdAt: new Date().toISOString(),
      }
      setMessages((prev) => [...prev, userMessage])

      try {
        const handle = await AnswersApiService.cable(question, conversationIdRef.current)
        activeUserMessageIdRef.current = handle.userMessageId
        markPending(handle.conversationId, handle.userMessageId)
        if (!conversationIdRef.current) {
          conversationIdRef.current = handle.conversationId
          onConversationCreated?.(handle.conversationId)
        }
      } catch (err) {
        setError((err as Error).message)
        resetStream()
      }
    },
    [isStreaming, onConversationCreated, resetStream, markPending],
  )

  const stop = useCallback(() => {
    const userMessageId = activeUserMessageIdRef.current
    if (userMessageId) {
      AnswersApiService.cancelCable(userMessageId).catch(() => {
        // best-effort; local state is reset regardless
      })
    }
    if (streamingContent) {
      const partial: Message = {
        id: `temp-${Date.now()}-stopped`,
        role: 'assistant',
        content: streamingContent,
        citations: null,
        audioUrl: null,
        createdAt: new Date().toISOString(),
      }
      setMessages((prev) => [...prev, partial])
    }
    resetStream()
  }, [streamingContent, resetStream])

  return { messages, streamingContent, isStreaming, error, sendMessage, stop }
}
